import { useEffect, useMemo, useRef, useState } from 'react';
import { RobotAvatar, type RobotMood } from './components/RobotAvatar';

type View = 'home' | 'chat';
type Message = { id: number; role: 'user' | 'assistant'; content: string };

type ChatResponse = { reply: string; mood?: RobotMood; fallback?: boolean };
type CreativityLevel = 'عملي' | 'مبتكر' | 'إبداعي' | 'مجنون' | 'خارج الحدود';

const quickPromptsBase = ['عطني قصيدة', 'حكمة اليوم', 'وش القصيدة اللي تناسب مزاجي'];
const initialMessage: Message = { id: 1, role: 'assistant', content: 'حي الله من جانا 👋 أنا أبو عبيد، وش ودك نسولف فيه؟' };

function isMorningTime(): boolean {
  const hour = new Date().getHours();
  return hour >= 5 && hour < 12;
}

function moodForText(text: string): RobotMood {
  const value = text.toLowerCase();
  if (/هههه|ضحك|نكت|😂/.test(value)) return 'laughing';
  if (/نعسان|نوم|تصبح|تعبان/.test(value)) return 'sleepy';
  if (/وش|ليش|كيف|اسألني|غريب/.test(value)) return 'curious';
  if (/طفشان|ملل|زعلان|ضايق/.test(value)) return 'grumpy';
  if (/حب|حلو|مبسوط|صباح الخير|مساء الخير/.test(value)) return 'happy';
  return 'neutral';
}

function App() {
  const [view, setView] = useState<View>(() => new URLSearchParams(window.location.search).get('view') === 'chat' ? 'chat' : 'home');
  const [messages, setMessages] = useState<Message[]>([initialMessage]);
  const [draft, setDraft] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [mood, setMood] = useState<RobotMood>('happy');
  const [aboutOpen, setAboutOpen] = useState(false);
  const [fallbackNotice, setFallbackNotice] = useState(false);
  const [creativity, setCreativity] = useState<CreativityLevel>('عملي');
  const [isMorning, setIsMorning] = useState(isMorningTime);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageCountLabel = useMemo(() => `${messages.length - 1} ${messages.length - 1 === 1 ? 'رسالة' : 'رسائل'}`, [messages.length]);
  const quickPrompts = [isMorning ? 'صباح الخير' : 'مساء الخير', ...quickPromptsBase];

  useEffect(() => {
    document.title = view === 'home' ? 'سوالف أبو عبيد' : 'السوالف مع أبو عبيد';
    window.history.replaceState({}, '', view === 'chat' ? '/?view=chat' : '/');
  }, [view]);

  useEffect(() => {
    const timer = window.setInterval(() => setIsMorning(isMorningTime()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isTyping]);

  useEffect(() => {
    const updateViewportHeight = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--visual-height', `${height}px`);
    };
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateViewportHeight);
    };
  }, []);

  const startChat = () => setView('chat');

  const clearConversation = () => {
    if (!window.confirm('متأكد تمسح السوالف؟ أبو عبيد ما يزعل... يمكن 😂')) return;
    setMessages([{ ...initialMessage, id: Date.now() }]);
    setMood('happy');
    setFallbackNotice(false);
  };

  const sendMessage = async (text = draft) => {
    const content = text.trim();
    if (!content || isTyping) return;
    setDraft('');
    setView('chat');
    setFallbackNotice(false);
    setMood(moodForText(content));
    const nextUserMessage: Message = { id: Date.now(), role: 'user', content };
    const nextMessages = [...messages, nextUserMessage];
    setMessages(nextMessages);
    setIsTyping(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creativity, messages: nextMessages.map(({ role, content: value }) => ({ role, content: value })) }),
      });
      const data = await response.json() as ChatResponse;
      if (!response.ok || !data.reply) throw new Error('تعذر الرد');
      setMessages((current) => [...current, { id: Date.now() + 1, role: 'assistant', content: data.reply }]);
      setMood(data.mood || moodForText(data.reply));
      setFallbackNotice(Boolean(data.fallback));
    } catch {
      setMessages((current) => [...current, { id: Date.now() + 1, role: 'assistant', content: 'يا بعدي شكلي علّقت شوي 😅 جرّب ترسلها مرة ثانية.' }]);
      setMood('grumpy');
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <main className={`site-shell ${view === 'chat' ? 'chat-mode' : ''}`}>
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="topbar">
        <button className="brand-lockup" onClick={() => setView('home')} aria-label="العودة للرئيسية">
          <span className="brand-mark"><span>ش</span></span>
          <span><strong>سوالف</strong><small>أبو عبيد</small></span>
        </button>
        <div className="top-actions">
          <span className="status-dot"><i /> متواجد ويسولف</span>
          <button className="text-button" onClick={() => setAboutOpen(true)}>عن أبو عبيد</button>
        </div>
      </header>

      {view === 'home' ? (
        <section className="home-view page-enter">
          <div className="home-copy">
            <span className="eyebrow"><span className="eyebrow-dot" /> خويك الروبوت السعودي</span>
            <h1>سوالف بسيطة،<br /><em>تعدل المزاج.</em></h1>
            <p className="hero-description">خوي روبوت، وعلومه طيبة. أبو عبيد روبوت سوالف، قصايد، مثقف، شجاع، كريم، ما يهاب لو كثرت عذاريبه — موجود 24/7.</p>
            <div className="home-actions">
              <button className="primary-button" onClick={startChat}><span>ابدأ السوالف</span><b>←</b></button>
              <button className="quiet-button" onClick={() => setAboutOpen(true)}>وش السالفة؟ <span>⌁</span></button>
            </div>
            <div className="mini-proof"><div className="mini-avatars"><span>س</span><span>م</span><span>ن</span></div><span>خويّك متواجد الحين، لا تستحي</span></div>
          </div>
          <div className="hero-robot-wrap">
            <div className="orbit orbit-one" /><div className="orbit orbit-two" />
            <div className="hero-note note-top"><span>☼</span> خفيف دم</div>
            <div className="hero-note note-bottom">يسمعك للآخر <span>↗</span></div>
            <RobotAvatar mood="happy" size="large" />
            <div className="hero-greeting"><span className="greeting-tail" />« حي الله من جانا 👋<br /><strong>أنا أبو عبيد، وش ودك نسولف فيه؟</strong> »</div>
          </div>
        </section>
      ) : (
        <section className="chat-view page-enter">
          <div className="chat-heading">
            <div className="chat-persona"><RobotAvatar mood={mood} size="small" speaking={isTyping} /><div><h1>السوالف مع أبو عبيد</h1><p><span className="live-dot" /> متواجد ويسولف معك</p></div></div>
            <button className="clear-button" onClick={clearConversation}><span>⌫</span> مسح المحادثة</button>
          </div>
          <div className="chat-card">
            <div className="chat-card-top"><span>جلسة سوالف اليوم</span><span className="message-count">{messageCountLabel}</span></div>
            <div className="messages-list" aria-live="polite">
              <div className="date-divider"><span>الحين</span></div>
              {messages.map((message) => <div className={`message-row ${message.role === 'user' ? 'message-user' : 'message-abu'}`} key={message.id}>
                {message.role === 'assistant' && <div className="message-avatar"><RobotAvatar mood={mood} size="small" /></div>}
                <div className="message-bubble"><p>{message.content}</p><time>{message.role === 'assistant' ? 'أبو عبيد' : 'أنت'}</time></div>
              </div>)}
              {isTyping && <div className="message-row message-abu"><div className="message-avatar"><RobotAvatar mood={mood} size="small" speaking /></div><div className="typing-bubble"><span>أبو عبيد يكتب</span><i /><i /><i /></div></div>}
              <div ref={bottomRef} />
            </div>
            {fallbackNotice && <div className="fallback-note">أبو عبيد يشتغل الحين بوضع السوالف السريع — الردود المحلية جاهزة لين تتصل خدمة الذكاء.</div>}
            <div className="composer-area">
              <div className="quick-prompts"><span className="quick-label">ابدأ من هنا</span>{quickPrompts.map((prompt) => <button key={prompt} onClick={() => sendMessage(prompt)} disabled={isTyping}>{prompt}</button>)}</div>
              <label className="creativity-control"><span>✨ مستوى الإبداع</span><select value={creativity} onChange={(event) => setCreativity(event.target.value as CreativityLevel)} disabled={isTyping} aria-label="مستوى الإبداع"><option value="عملي">🟢 عملي</option><option value="مبتكر">🔵 مبتكر</option><option value="إبداعي">🟣 إبداعي</option><option value="مجنون">🔴 مجنون</option><option value="خارج الحدود">🟡 خارج الحدود</option></select></label>
              <form className="composer" onSubmit={(event) => { event.preventDefault(); void sendMessage(); }}>
                <button className="mic-button" type="button" disabled aria-label="المايك غير متاح">♩<span>قريباً</span></button>
                <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="اكتب اللي بخاطرك..." aria-label="اكتب رسالتك" disabled={isTyping} />
                <button className="send-button" type="submit" disabled={!draft.trim() || isTyping} aria-label="إرسال الرسالة">↑</button>
              </form>
              <p className="composer-hint">أبو عبيد خوي سوالف، مو مستشار طبي أو قانوني أو مالي.</p>
            </div>
          </div>
        </section>
      )}

      <footer className="footer"><span>© سوالف أبو عبيد</span><span>مصنوع للسوالف الطيبة <b>✦</b></span></footer>

      {aboutOpen && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setAboutOpen(false); }}>
        <section className="about-modal" role="dialog" aria-modal="true" aria-labelledby="about-title">
          <button className="modal-close" onClick={() => setAboutOpen(false)} aria-label="إغلاق">×</button>
          <RobotAvatar mood="happy" size="medium" />
          <span className="eyebrow">من هو أبو عبيد؟</span>
          <h2 id="about-title">خوي روبوت، وعلومه طيبة.</h2>
          <p>أبو عبيد روبوت سوالف، قصايد، مثقف، شجاع، كريم، ما يهاب لو كثرت عذاريبه — موجود 24/7.</p>
          <button className="primary-button modal-cta" onClick={() => { setAboutOpen(false); startChat(); }}>خلنا نسولف <b>←</b></button>
        </section>
      </div>}
    </main>
  );
}

export default App;
